import React from 'react';
import './Legal.css';

function Privacy() {
  return (
    <div className="legal-page">
      <section className="legal-hero">
        <div className="legal-bg-elements">
          <div className="legal-glow legal-glow-1"></div>
          <div className="legal-glow legal-glow-2"></div>
        </div>
        <div className="container">
          <h1>Privacy Policy</h1>
          <p className="legal-updated">Last updated: January 2025</p>
        </div>
      </section>

      <section className="section legal-content-section">
        <div className="container">
          <div className="legal-content">
            <div className="legal-intro">
              <p>
                Play Best Entertainment, LLC ("Company", "we", "us", or "our") is committed to protecting your
                privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information
                when you use our Wordalanche mobile application ("App").
              </p>
              <p>
                Please read this Privacy Policy carefully. By using the App, you agree to the collection and
                use of information in accordance with this policy.
              </p>
            </div>

            <div className="legal-section">
              <h2>1. Information We Collect</h2>

              <h3>Information You Provide</h3>
              <p>We may collect information that you voluntarily provide, including:</p>
              <ul>
                <li>Account information (if you create an account or sign in via Game Center)</li>
                <li>Support requests and correspondence</li>
                <li>Feedback and survey responses</li>
              </ul>

              <h3>Information Collected Automatically</h3>
              <p>When you use the App, we may automatically collect:</p>
              <ul>
                <li>Device information (device type, operating system version, unique device identifiers)</li>
                <li>Usage data (game progress, scores, achievements, play time)</li>
                <li>Log data (access times, features used, app crashes)</li>
                <li>Advertising identifiers (IDFA) for personalized advertising, with your consent</li>
              </ul>

              <h3>Information from Third Parties</h3>
              <p>
                We may receive information from third-party services such as Apple Game Center (for leaderboards
                and achievements) and advertising partners (for ad attribution and analytics).
              </p>
            </div>

            <div className="legal-section">
              <h2>2. How We Use Your Information</h2>
              <p>We use the collected information for the following purposes:</p>

              <table className="data-table">
                <thead>
                  <tr>
                    <th>Purpose</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Provide Services</strong></td>
                    <td>Operate the App, save game progress, sync across devices</td>
                  </tr>
                  <tr>
                    <td><strong>Improve the App</strong></td>
                    <td>Analyze usage patterns, fix bugs, develop new features</td>
                  </tr>
                  <tr>
                    <td><strong>Personalization</strong></td>
                    <td>Customize your experience, remember preferences</td>
                  </tr>
                  <tr>
                    <td><strong>Advertising</strong></td>
                    <td>Display relevant ads, measure ad performance</td>
                  </tr>
                  <tr>
                    <td><strong>Communication</strong></td>
                    <td>Respond to support requests, send important notices</td>
                  </tr>
                  <tr>
                    <td><strong>Legal Compliance</strong></td>
                    <td>Comply with legal obligations, enforce our terms</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="legal-section">
              <h2>3. Advertising</h2>
              <p>
                We use third-party advertising partners, including Google AdMob, to display advertisements
                in the App. These partners may collect and use certain information about your device and
                app usage to provide personalized advertising.
              </p>
              <p>
                You can opt out of personalized advertising through your device settings:
              </p>
              <ul>
                <li><strong>iOS:</strong> Settings &gt; Privacy &gt; Tracking &gt; Turn off "Allow Apps to Request to Track"</li>
              </ul>
              <p>
                For more information about how Google uses data, please visit:{' '}
                <a href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noopener noreferrer">
                  Google's Partner Sites Policy
                </a>
              </p>
            </div>

            <div className="legal-section">
              <h2>4. Data Sharing</h2>
              <p>We may share your information in the following circumstances:</p>
              <ul>
                <li>
                  <strong>Service Providers:</strong> With vendors who help us operate the App (analytics,
                  advertising, cloud hosting)
                </li>
                <li>
                  <strong>Legal Requirements:</strong> When required by law, legal process, or government request
                </li>
                <li>
                  <strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets
                </li>
                <li>
                  <strong>Protection of Rights:</strong> To protect our rights, privacy, safety, or property
                </li>
                <li>
                  <strong>With Your Consent:</strong> For any other purpose with your explicit consent
                </li>
              </ul>
              <p>
                We do not sell your personal information to third parties.
              </p>
            </div>

            <div className="legal-section">
              <h2>5. Data Retention</h2>
              <p>
                We retain your information for as long as necessary to provide the App and fulfill the purposes
                described in this Privacy Policy. Game progress and account data are retained while you have
                the App installed and for a reasonable period afterward.
              </p>
              <p>
                You can delete your game data by uninstalling the App or contacting us to request deletion.
              </p>
            </div>

            <div className="legal-section">
              <h2>6. Data Security</h2>
              <p>
                We implement appropriate technical and organizational measures to protect your information
                against unauthorized access, alteration, disclosure, or destruction. However, no method of
                transmission over the internet or electronic storage is 100% secure.
              </p>
            </div>

            <div className="legal-section">
              <h2>7. Children's Privacy</h2>
              <p>
                The App is not directed to children under 13 years of age. We do not knowingly collect personal
                information from children under 13. If we become aware that we have collected personal information
                from a child under 13, we will take steps to delete that information.
              </p>
              <p>
                If you are a parent or guardian and believe your child has provided us with personal information,
                please contact us immediately.
              </p>
            </div>

            <div className="legal-section">
              <h2>8. Your Rights</h2>
              <p>Depending on your location, you may have certain rights regarding your personal information:</p>
              <ul>
                <li><strong>Access:</strong> Request a copy of your personal information</li>
                <li><strong>Correction:</strong> Request correction of inaccurate information</li>
                <li><strong>Deletion:</strong> Request deletion of your personal information</li>
                <li><strong>Portability:</strong> Request your data in a portable format</li>
                <li><strong>Opt-out:</strong> Opt out of personalized advertising</li>
              </ul>
              <p>
                To exercise these rights, please contact us using the information provided below.
              </p>
            </div>

            <div className="legal-section">
              <h2>9. California Privacy Rights</h2>
              <p>
                If you are a California resident, you have additional rights under the California Consumer
                Privacy Act (CCPA):
              </p>
              <ul>
                <li>Right to know what personal information is collected</li>
                <li>Right to know whether personal information is sold or disclosed</li>
                <li>Right to opt out of the sale of personal information</li>
                <li>Right to non-discrimination for exercising your rights</li>
              </ul>
              <p>
                We do not sell personal information as defined under the CCPA.
              </p>
            </div>

            <div className="legal-section">
              <h2>10. International Data Transfers</h2>
              <p>
                Your information may be transferred to and processed in countries other than your country of
                residence. These countries may have different data protection laws. By using the App, you consent
                to such transfers.
              </p>
            </div>

            <div className="legal-section">
              <h2>11. Third-Party Links</h2>
              <p>
                The App may contain links to third-party websites or services. We are not responsible for the
                privacy practices of these third parties. We encourage you to review their privacy policies.
              </p>
            </div>

            <div className="legal-section">
              <h2>12. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. We will notify you of significant changes
                by posting the updated policy within the App or on our website. Your continued use of the App
                after such changes constitutes acceptance of the updated policy.
              </p>
            </div>

            <div className="legal-section">
              <h2>13. Contact Us</h2>
              <p>
                If you have questions or concerns about this Privacy Policy or our data practices, please
                contact us at:
              </p>
              <div className="contact-info">
                <p><strong>Play Best Entertainment, LLC</strong></p>
                <p>Email: <a href="mailto:privacy@playbestentertainment.com">privacy@playbestentertainment.com</a></p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Privacy;
